import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Check, 
  CheckCheck, 
  X, 
  RefreshCw, 
  CreditCard, 
  FileText, 
  Shield, 
  Download, 
  Lock, 
  Info, 
  Gift,
  ChevronRight,
  Loader2,
  Wifi,
  WifiOff,
  Clock,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { useNotifications } from '../contexts/NotificationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping based on notification type
const iconMap = {
  'refresh-cw': RefreshCw,
  'credit-card': CreditCard,
  'check-circle': Check,
  'file-text': FileText,
  'download': Download,
  'shield': Shield,
  'lock': Lock,
  'info': Info,
  'gift': Gift,
  'clock': Clock,
  'dollar-sign': DollarSign,
  'alert-circle': AlertCircle
};

// Color mapping
const colorMap = {
  'blue': 'bg-blue-100 text-blue-600',
  'amber': 'bg-amber-100 text-amber-600',
  'green': 'bg-green-100 text-green-600',
  'red': 'bg-red-100 text-red-600',
  'purple': 'bg-purple-100 text-purple-600',
  'slate': 'bg-slate-100 text-slate-600',
  'yellow': 'bg-yellow-100 text-yellow-600',
  'emerald': 'bg-emerald-100 text-emerald-600'
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  // Get real-time notifications from context
  const { 
    notifications: realtimeNotifications, 
    unreadCount: realtimeUnreadCount, 
    isConnected,
    markAsRead: markRealtimeAsRead,
    markAllAsRead: markAllRealtimeAsRead
  } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const getToken = () => {
    return localStorage.getItem('losspay_token');
  };

  const fetchUnreadCount = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error('Failed to fetch notification count:', err);
    }
  };

  const fetchNotifications = async () => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (notificationId) => {
    const token = getToken();
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    const token = getToken();
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const dismissNotification = async (e, notificationId) => {
    e.stopPropagation();
    const token = getToken();
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Navigate if there's an action URL
    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Info;
    return IconComponent;
  };

  // Combine counts from both persistent and real-time notifications
  const totalUnreadCount = unreadCount + realtimeUnreadCount;

  // Handle mark all read for both types
  const handleMarkAllAsRead = () => {
    markAllAsRead();
    markAllRealtimeAsRead();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        data-testid="notification-bell"
        title={isConnected ? 'Real-time updates active' : 'Real-time updates connecting...'}
      >
        <Bell className="h-5 w-5" />
        {totalUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
          </span>
        )}
        {/* Real-time connection indicator */}
        <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
          isConnected ? 'bg-green-500' : 'bg-slate-300'
        }`} title={isConnected ? 'Connected' : 'Connecting...'} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">Notifications</h3>
              {isConnected && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <Wifi className="h-3 w-3" />
                  Live
                </span>
              )}
            </div>
            {totalUnreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-[#3B5BDB] hover:text-[#2f4bc0] font-medium flex items-center gap-1"
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Real-time Notifications Section */}
          {realtimeNotifications.length > 0 && (
            <div className="border-b border-slate-200">
              <div className="px-4 py-2 bg-blue-50">
                <p className="text-xs font-medium text-blue-700 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Recent Updates
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {realtimeNotifications.slice(0, 3).map((notification) => {
                  const colorClass = notification.data?.new_status 
                    ? (colorMap[notification.data.status_color] || colorMap.blue)
                    : colorMap.blue;
                  
                  return (
                    <div
                      key={notification.id}
                      onClick={() => {
                        if (!notification.read) {
                          markRealtimeAsRead(notification.id);
                        }
                        if (notification.data?.action_url) {
                          setIsOpen(false);
                          navigate(notification.data.action_url);
                        }
                      }}
                      className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                        !notification.read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Bell className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notification.read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <span className="text-xs text-slate-400 mt-1 block">
                            {formatTime(notification.timestamp)}
                          </span>
                        </div>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-[#3B5BDB] rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : notifications.length === 0 && realtimeNotifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No notifications yet</p>
              </div>
            ) : notifications.length === 0 ? (
              null
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification) => {
                  const IconComponent = getIcon(notification.icon);
                  const colorClass = colorMap[notification.color] || colorMap.slate;
                  
                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                        !notification.is_read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${!notification.is_read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                              {notification.title}
                            </p>
                            <button
                              onClick={(e) => dismissNotification(e, notification.id)}
                              className="text-slate-400 hover:text-slate-600 p-1 -mr-1"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-slate-400">
                              {formatTime(notification.created_at)}
                            </span>
                            {notification.action_label && (
                              <span className="text-xs text-[#3B5BDB] font-medium flex items-center gap-1">
                                {notification.action_label}
                                <ChevronRight className="h-3 w-3" />
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Unread indicator */}
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-[#3B5BDB] rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
              <Button
                variant="ghost"
                className="w-full text-[#3B5BDB] hover:text-[#2f4bc0] hover:bg-blue-50"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
              >
                View All Notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
